import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart,
  Area,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useAuth } from "@/admin/AuthContext";
import {
  UserRound,
  Newspaper,
  Stethoscope,
  Image as ImageIcon,
  FileBarChart2,
  Mail,
  Activity,
  BedDouble,
  TrendingUp,
  TrendingDown,
  Loader2,
  ArrowRight,
  Clock,
} from "lucide-react";

const fmtNum = (n) =>
  typeof n === "number" ? n.toLocaleString("id-ID") : n ?? "—";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return String(iso);
  }
};

const MONTHS_ID = [
  "",
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const MONTHS_SHORT_ID = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mei",
  "Jun",
  "Jul",
  "Ags",
  "Sep",
  "Okt",
  "Nov",
  "Des",
];

const STAT_CARDS = [
  { key: "doctors", label: "Dokter", icon: UserRound, to: "/admin/doctors", color: "bg-blue-600" },
  { key: "news", label: "Berita", icon: Newspaper, to: "/admin/news", color: "bg-emerald-600" },
  { key: "services", label: "Layanan", icon: Stethoscope, to: "/admin/services", color: "bg-indigo-600" },
  { key: "gallery", label: "Galeri", icon: ImageIcon, to: "/admin/gallery", color: "bg-fuchsia-600" },
  { key: "laporan", label: "Laporan", icon: FileBarChart2, to: "/admin/laporan", color: "bg-amber-600" },
  { key: "contacts", label: "Pesan Kontak", icon: Mail, to: "/admin/contacts", color: "bg-rose-600" },
];

export default function DashboardHome() {
  const { authAxios, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    authAxios
      .get("/admin/dashboard/summary")
      .then((r) => active && setData(r.data))
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [authAxios]);

  const counts = data?.counts || {};
  const kunjungan = data?.kunjungan_terbaru;
  const trend = (data?.kunjungan_trend || []).map((x) => ({
    name: MONTHS_SHORT_ID[x.month] || String(x.month),
    total: x.total,
  }));
  const bor = data?.bor_terbaru;
  const borTrend = (data?.bor_trend || []).map((x) => ({
    name: MONTHS_SHORT_ID[x.month] || String(x.month),
    bor: x.bor,
  }));
  const recentNews = data?.recent_news || [];
  const recentContacts = data?.recent_contacts || [];

  if (loading) {
    return (
      <div className="py-24 flex items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl" data-testid="admin-dashboard-home">
      {/* Greeting */}
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900">
          Selamat datang{user?.name ? `, ${user.name}` : ""} 👋
        </h1>
        <p className="text-sm text-slate-500">
          Ringkasan singkat konten &amp; aktivitas RS Bhayangkara Tarakan.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="dashboard-stat-cards">
        {STAT_CARDS.map((c) => (
          <Link
            key={c.key}
            to={c.to}
            data-testid={`dashboard-stat-${c.key}`}
            className="group bg-white rounded-2xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-md transition"
          >
            <div className="flex items-center justify-between">
              <div className={`w-11 h-11 rounded-xl ${c.color} text-white flex items-center justify-center`}>
                <c.icon className="w-5 h-5" />
              </div>
              {c.key === "contacts" && counts.contacts_unread > 0 ? (
                <span
                  data-testid="dashboard-contacts-unread"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500 text-white text-[11px] font-bold"
                >
                  {counts.contacts_unread} baru
                </span>
              ) : (
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition" />
              )}
            </div>
            <div className="mt-3 text-3xl font-bold text-slate-900">
              {fmtNum(counts[c.key])}
            </div>
            <div className="text-sm text-slate-500">{c.label}</div>
          </Link>
        ))}
      </div>

      {/* Kunjungan & BOR trend */}
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        {/* Kunjungan terbaru */}
        <div className="bg-gradient-to-br from-blue-800 to-blue-900 rounded-2xl p-6 text-white" data-testid="dashboard-kunjungan">
        <div className="flex items-center gap-2 text-blue-200 text-sm mb-1">
          <Activity className="w-4 h-4" />
          Kunjungan Pasien Terbaru
        </div>
        {kunjungan && kunjungan.total != null ? (
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <div className="text-4xl font-bold">{fmtNum(kunjungan.total)}</div>
              <div className="text-blue-200 text-sm">
                {MONTHS_ID[kunjungan.month] || ""} {kunjungan.year}
              </div>
            </div>
            {kunjungan.growth_pct != null && (
              <div
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold ${
                  kunjungan.growth_pct >= 0
                    ? "bg-emerald-500/20 text-emerald-200"
                    : "bg-rose-500/20 text-rose-200"
                }`}
              >
                {kunjungan.growth_pct >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                {kunjungan.growth_pct >= 0 ? "+" : ""}
                {kunjungan.growth_pct}% YoY
              </div>
            )}
            <Link
              to="/laporan/analisis/kunjungan"
              className="ml-auto inline-flex items-center gap-1 text-sm text-blue-100 hover:text-white"
            >
              Lihat analisis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="text-blue-200 text-sm">Data kunjungan belum tersedia.</div>
        )}

        {trend.length > 1 && (
          <div className="mt-4" data-testid="dashboard-kunjungan-trend">
            <div className="text-xs text-blue-200 mb-1">
              Tren 6 bulan terakhir
            </div>
            <div style={{ width: "100%", height: 130 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 5, right: 8, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="kunjColor" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#93c5fd" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#93c5fd" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#bfdbfe", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#0f172a",
                      border: "none",
                      borderRadius: 8,
                      color: "#fff",
                      fontSize: 12,
                    }}
                    labelStyle={{ color: "#bfdbfe" }}
                    formatter={(v) => [Number(v).toLocaleString("id-ID"), "Kunjungan"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#e0f2fe"
                    strokeWidth={2.5}
                    fill="url(#kunjColor)"
                    dot={{ r: 3, fill: "#e0f2fe" }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        </div>

        {/* BOR terbaru */}
        <div className="bg-gradient-to-br from-teal-700 to-teal-900 rounded-2xl p-6 text-white" data-testid="dashboard-bor">
          <div className="flex items-center gap-2 text-teal-200 text-sm mb-1">
            <BedDouble className="w-4 h-4" />
            BOR (Bed Occupancy Rate) Terbaru
          </div>
          {bor && bor.bor != null ? (
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <div className="text-4xl font-bold">{bor.bor}%</div>
                <div className="text-teal-200 text-sm">
                  {MONTHS_ID[bor.month] || ""} {bor.year}
                </div>
              </div>
              <Link
                to="/laporan/analisis/bor"
                className="ml-auto inline-flex items-center gap-1 text-sm text-teal-100 hover:text-white"
              >
                Lihat analisis <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="text-teal-200 text-sm">Data BOR belum tersedia.</div>
          )}

          {borTrend.length > 1 && (
            <div className="mt-4" data-testid="dashboard-bor-trend">
              <div className="text-xs text-teal-200 mb-1">
                Tren 6 bulan terakhir
              </div>
              <div style={{ width: "100%", height: 130 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={borTrend} margin={{ top: 5, right: 8, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="borColor" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#5eead4" stopOpacity={0.7} />
                        <stop offset="100%" stopColor="#5eead4" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="name"
                      tick={{ fill: "#99f6e4", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#0f172a",
                        border: "none",
                        borderRadius: 8,
                        color: "#fff",
                        fontSize: 12,
                      }}
                      labelStyle={{ color: "#99f6e4" }}
                      formatter={(v) => [`${v}%`, "BOR"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="bor"
                      stroke="#ccfbf1"
                      strokeWidth={2.5}
                      fill="url(#borColor)"
                      dot={{ r: 3, fill: "#ccfbf1" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent lists */}
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        {/* Recent news */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="dashboard-recent-news">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              <Newspaper className="w-4 h-4 text-slate-500" />
              Berita Terbaru
            </h2>
            <Link to="/admin/news" className="text-xs text-blue-700 hover:underline">
              Kelola
            </Link>
          </div>
          {recentNews.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Belum ada berita.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recentNews.map((n) => (
                <li key={n.id} className="py-2.5">
                  <div className="text-sm font-medium text-slate-800 line-clamp-1">
                    {n.title_id || n.title_en || "(tanpa judul)"}
                  </div>
                  <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" />
                    {fmtDate(n.date)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent contacts */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="dashboard-recent-contacts">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-500" />
              Pesan Kontak Terbaru
            </h2>
            <Link to="/admin/contacts" className="text-xs text-blue-700 hover:underline">
              Lihat semua
            </Link>
          </div>
          {recentContacts.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Belum ada pesan masuk.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recentContacts.map((c) => (
                <li key={c.id} className="py-2.5">
                  <div className="text-sm font-medium text-slate-800 line-clamp-1">
                    {c.name || "Anonim"}
                    {c.subject ? (
                      <span className="text-slate-400 font-normal"> — {c.subject}</span>
                    ) : null}
                  </div>
                  <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" />
                    {fmtDate(c.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
